import { useState } from 'react';
import api from '../services/api';

function formatDateForInput(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  // Format: YYYY-MM-DDTHH:mm
  return d.toISOString().slice(0, 16);
}

export default function EventForm({ onSuccess, onCancel, initialData = null, mode = 'create' }) {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    description: initialData?.description || '',
    venueName: initialData?.venueName || initialData?.venue_name || '',
    city: initialData?.city || initialData?.location?.city || '',
    country: initialData?.country || initialData?.location?.country || 'France',
    eventDate: formatDateForInput(initialData?.eventDate || initialData?.event_date) || '',
    endDate: formatDateForInput(initialData?.endDate || initialData?.end_date) || '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  function validateForm() {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    } else if (formData.title.length > 200) {
      newErrors.title = 'Le titre ne doit pas dépasser 200 caractères';
    }

    if (!formData.eventDate) {
      newErrors.eventDate = 'La date de début est requise';
    } else if (mode === 'create' && new Date(formData.eventDate) < new Date()) {
      newErrors.eventDate = 'La date doit être dans le futur';
    }

    if (formData.endDate && formData.eventDate && new Date(formData.endDate) <= new Date(formData.eventDate)) {
      newErrors.endDate = 'La date de fin doit être après la date de début';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'La ville est requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  }

  async function handleSubmit(e, status = 'draft') {
    e.preventDefault();
    setApiError(null);

    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        venueName: formData.venueName.trim(),
        city: formData.city.trim(),
        country: formData.country.trim(),
        eventDate: formData.eventDate,
      };

      if (formData.endDate) {
        payload.endDate = formData.endDate;
      }

      if (mode === 'edit' && initialData?.id) {
        await api.put(`/events/${initialData.id}`, payload);
      } else {
        const res = await api.post('/events', payload);

        // Si on veut publier directement
        if (status === 'published' && res.data?.event?.id) {
          await api.patch(`/events/${res.data.event.id}/status`, { status: 'published' });
        }
      }

      onSuccess?.();
    } catch (err) {
      setApiError(err.message || (mode === 'edit' ? 'Erreur lors de la mise à jour' : 'Erreur lors de la création'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={(e) => handleSubmit(e, 'draft')} className="space-y-6">
      {apiError && (
        <div className="border border-status-rejected/30 text-status-rejected px-3 py-2 text-sm font-mono">
          {apiError}
        </div>
      )}

      {/* Titre */}
      <div>
        <label htmlFor="eventTitle" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Titre <span className="text-accent">*</span>
        </label>
        <input
          type="text"
          id="eventTitle"
          name="title"
          value={formData.title}
          onChange={handleChange}
          maxLength={200}
          placeholder="Nom de l'événement"
          className="th-input"
        />
        {errors.title && <p className="text-status-rejected text-xs mt-1 font-mono">{errors.title}</p>}
      </div>

      {/* Description */}
      <div>
        <label htmlFor="eventDesc" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Description
        </label>
        <textarea
          id="eventDesc"
          name="description"
          value={formData.description}
          onChange={handleChange}
          maxLength={5000}
          rows={4}
          placeholder="Décrivez votre événement (optionnel)"
          className="th-input resize-none"
        />
      </div>

      {/* Lieu */}
      <div>
        <label htmlFor="venueName" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Lieu / Salle
        </label>
        <input
          type="text"
          id="venueName"
          name="venueName"
          value={formData.venueName}
          onChange={handleChange}
          placeholder="ex: Warehouse 42 (optionnel)"
          className="th-input"
        />
      </div>

      {/* Ville + Pays */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="eventCity" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
            Ville <span className="text-accent">*</span>
          </label>
          <input
            type="text"
            id="eventCity"
            name="city"
            value={formData.city}
            onChange={handleChange}
            placeholder="ex: Paris"
            className="th-input"
          />
          {errors.city && <p className="text-status-rejected text-xs mt-1 font-mono">{errors.city}</p>}
        </div>
        <div>
          <label htmlFor="eventCountry" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
            Pays
          </label>
          <input
            type="text"
            id="eventCountry"
            name="country"
            value={formData.country}
            onChange={handleChange}
            placeholder="ex: France"
            className="th-input"
          />
        </div>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="eventDate" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
            Date de début <span className="text-accent">*</span>
          </label>
          <input
            type="datetime-local"
            id="eventDate"
            name="eventDate"
            value={formData.eventDate}
            onChange={handleChange}
            className="bg-transparent border th-border th-text text-sm focus:border-accent outline-none transition-colors duration-150 px-3 py-2.5 w-full dark:[color-scheme:dark]"
          />
          {errors.eventDate && <p className="text-status-rejected text-xs mt-1 font-mono">{errors.eventDate}</p>}
        </div>
        <div>
          <label htmlFor="endDate" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
            Date de fin
          </label>
          <input
            type="datetime-local"
            id="endDate"
            name="endDate"
            value={formData.endDate}
            onChange={handleChange}
            className="bg-transparent border th-border th-text text-sm focus:border-accent outline-none transition-colors duration-150 px-3 py-2.5 w-full dark:[color-scheme:dark]"
          />
          {errors.endDate && <p className="text-status-rejected text-xs mt-1 font-mono">{errors.endDate}</p>}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        {mode === 'edit' ? (
          <button
            type="submit"
            disabled={loading}
            className="flex-1 th-btn-accent disabled:opacity-50"
          >
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        ) : (
          <>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 th-btn disabled:opacity-50"
            >
              {loading ? 'Création...' : 'Sauvegarder en brouillon'}
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={(e) => handleSubmit(e, 'published')}
              className="flex-1 th-btn-accent disabled:opacity-50"
            >
              {loading ? 'Création...' : 'Publier directement'}
            </button>
          </>
        )}
      </div>

      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="w-full border border-status-rejected/30 text-status-rejected text-sm font-mono uppercase tracking-widest px-3 py-2.5 hover:bg-status-rejected/10 transition-colors duration-150"
        >
          Annuler
        </button>
      )}
    </form>
  );
}
